/**
 * Planos de manutenção — e são **duas coisas com o mesmo nome**.
 *
 * O **plano novo** (`api/planos`) é o que se publica: 208 planos, cada um com os
 * veículos em que vale e os conteúdos que o compõem (6.394 no total, média de 31
 * e máximo de 68 por plano). Os conteúdos saem de uma biblioteca de **216
 * textos** reaproveitáveis — "Inspecionar estado de saúde da bateria" é escrito
 * uma vez e usado em muitos planos.
 *
 * O **grupo de manutenção** (`api/planos-manutencao/grupos`) é o formato antigo,
 * por veículo e versão: 1.222 grupos guardando **582.249 itens**. A média é de
 * 493 itens por grupo e o maior tem **15.283** — e a rota que lista os itens de
 * um grupo **não pagina**: ela devolve 3,9 MB numa resposta só no pior caso.
 * A tela avisa antes de abrir, porque três segundos de espera sem explicação
 * parecem travamento.
 */

import { useEffect, useMemo, useState } from "react"
import {
  type ConteudoDoPlano, type GrupoDeManutencao, type ItemDeManutencao, type Plano, type TextoDePlano, type VeiculoVinculado, usarFilhos, usarGravar, usarIncluir, usarMestre, usarMestreInteiro, usarPublicacao,
} from "@/api/outrosProjetos"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { ErroDaApi } from "@/api/erros"
import { casaBusca } from "@/kit/buscaDeTexto"
import { ordenarLista, usarOrdemDaLista } from "@/kit/ordemDaLista"
import {
  Aviso, Botao, Campo, ColunaRecolhivel, Escolha, EstadoDePublicacao, FiltroDaLista, ItemDaLista, Linha, Painel, Secao, TabelaSimples, Vazio, anosInvertidos, estiloCampo, mensagem, nomeOu, usarNomes, usarTermoAdiado,
} from "./pecas"
import { LoteDoPlano } from "./LoteDoPlano"

type Visao = "planos" | "textos" | "grupos"

const VISOES: { chave: Visao; rotulo: string }[] = [
  { chave: "planos", rotulo: "Planos" },
  { chave: "textos", rotulo: "Biblioteca de textos" },
  { chave: "grupos", rotulo: "Grupos por veículo" },
]

const VEICULO_VAZIO = { veiculoId: 0, motorId: 0, anoInicial: 0, anoFinal: 0 }

export function Planos() {
  const [visao, setVisao] = useState<Visao>("planos")
  const [recado, setRecado] = useState<string | null>(null)
  const nomes = usarNomes()

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa titulo="Planos de manutenção">
        <div style={{ display: "flex", gap: 2, background: "var(--surface2)", padding: 2, borderRadius: 6 }}>
          {VISOES.map((v) => (
            <button
              key={v.chave}
              onClick={() => { setVisao(v.chave); setRecado(null) }}
              style={{
                border: 0, borderRadius: 4, padding: "4px 10px", fontSize: 12, cursor: "pointer",
                background: visao === v.chave ? "var(--surface)" : "transparent",
                color: visao === v.chave ? "var(--txt)" : "var(--txt2)",
                fontWeight: visao === v.chave ? 600 : 400,
              }}
            >
              {v.rotulo}
            </button>
          ))}
        </div>
      </Faixa>

      <AvisoDeDadoGlobal oQueMuda="Os planos são conteúdo publicado" />

      {visao === "planos" && <AbaDePlanos nomes={nomes} aoAvisar={setRecado} />}
      {visao === "textos" && <AbaDeTextos aoAvisar={setRecado} />}
      {visao === "grupos" && <AbaDeGrupos nomes={nomes} aoAvisar={setRecado} />}

      <BarraDeEstado>{recado ?? ""}</BarraDeEstado>
    </div>
  )
}

// ------------------------------------------------------------------ planos

function AbaDePlanos({ nomes, aoAvisar }: {
  nomes: ReturnType<typeof usarNomes>
  aoAvisar: (m: string | null) => void
}) {
  const [aberto, setAberto] = useState<Plano | null>(null)
  const [incluindo, setIncluindo] = useState(false)
  const [busca, setBusca] = useState("")
  const termo = usarTermoAdiado(busca)
  // Vêm 200 de 208 planos por página: a alfabética é feita na API, senão os que ficam
  // fora da página nunca entrariam na ordem.
  const [ordem, setOrdem] = usarOrdemDaLista("planos")
  const lista = usarMestre<Plano>("planos", "api/planos", {
    busca: termo || undefined, ordem: ordem === "alfabetica" ? "alfabetica" : undefined, pageSize: 200,
  })

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
      <ColunaRecolhivel chave="planos" titulo="Planos" largura={310}
                        detalhe={lista.isPending ? undefined
                          : `${lista.itens.length < lista.total ? `${lista.itens.length} de ` : ""}${lista.total}`}
                        topo={
                          <>
                            {/* O botao fica na coluna da aba, e nao na faixa do topo: a faixa e das
                                tres abas, e incluir plano nao e o mesmo que incluir texto ou grupo. */}
                            <div>
                              <Botao destaque aoClicar={() => { aoAvisar(null); setAberto(null); setIncluindo(true) }}>
                                Incluir plano
                              </Botao>
                            </div>
                            <FiltroDaLista valor={busca} aoMudar={setBusca} rotulo="Filtrar planos"
                                           placeholder="Título do plano…"
                                           ordem={ordem} aoMudarOrdem={setOrdem} />
                          </>
                        }>
        {lista.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
        ) : lista.error ? (
          <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
            {lista.error instanceof ErroDaApi ? lista.error.message : "Não foi possível ler."}
          </p>
        ) : lista.itens.length === 0 ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>{termo ? "Nenhum plano com esse texto." : "Nenhum plano."}</p>
        ) : (
          lista.itens.map((p) => (
            <ItemDaLista
              key={p.id}
              ativo={aberto?.id === p.id}
              marcado={p.publicado}
              aoClicar={() => setAberto(p)}
              titulo={p.titulo || `Plano ${p.id}`}
              detalhe={p.montadora ? "da montadora" : "próprio"}
            />
          ))
        )}
      </ColunaRecolhivel>

      {incluindo ? (
        <InclusaoDoPlano
          aoAvisar={aoAvisar}
          aoFechar={() => setIncluindo(false)}
          aoCriar={(p) => { setIncluindo(false); setAberto(p) }}
        />
      ) : aberto ? (
        <FichaDoPlano plano={aberto} nomes={nomes} aoAvisar={aoAvisar} aoFechar={() => setAberto(null)} />
      ) : (
        <Vazio>
          São 208 planos. Cada um reúne os conteúdos de revisão de um veículo, tirados da
          biblioteca de textos — que fica na aba ao lado.
        </Vazio>
      )}
    </div>
  )
}

/**
 * Incluir plano de manutenção.
 *
 * Só o título é obrigatório na API; "da montadora", manual e página são os campos que a
 * ficha também mostra, e cabem aqui porque são de uma linha cada.
 *
 * **`migrado` não aparece, de propósito.** É marca de quem veio da carga antiga, não
 * escolha de quem cadastra — e a ficha de edição também não o expõe. Campo de máquina
 * em formulário de gente é convite a marcar por engano.
 */
function InclusaoDoPlano({ aoAvisar, aoFechar, aoCriar }: {
  aoAvisar: (m: string | null) => void
  aoFechar: () => void
  aoCriar: (p: Plano) => void
}) {
  const incluir = usarIncluir<Plano>([["outros", "planos"]])
  const [forma, setForma] = useState({ titulo: "", montadora: false, manual: "", pagina: "" })

  return (
    <Painel>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
        <b style={{ fontSize: 13.5 }}>Incluir plano</b>
        <Xis aoClicar={aoFechar} />
      </div>

      <Secao titulo="O plano">
        <Campo rotulo="Título" ajuda="Costuma trazer o veículo e a geração.">
          <input autoFocus value={forma.titulo} style={estiloCampo}
                 onChange={(e) => setForma({ ...forma, titulo: e.target.value })} />
        </Campo>
        <Linha>
          <Campo rotulo="Manual" ajuda="Endereço do manual de origem.">
            <input value={forma.manual} style={estiloCampo}
                   onChange={(e) => setForma({ ...forma, manual: e.target.value })} />
          </Campo>
          <Campo rotulo="Página" ajuda="Onde está no manual da montadora.">
            <input value={forma.pagina} style={estiloCampo}
                   onChange={(e) => setForma({ ...forma, pagina: e.target.value })} />
          </Campo>
        </Linha>
        <Campo rotulo="Da montadora">
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, paddingTop: 5 }}>
            <input type="checkbox" checked={forma.montadora}
                   onChange={(e) => setForma({ ...forma, montadora: e.target.checked })} />
            {forma.montadora ? "da montadora" : "próprio"}
          </label>
        </Campo>

        <p style={{ margin: "2px 0 10px", fontSize: 11, color: "var(--txt3)", lineHeight: 1.5,
                    maxWidth: "70ch" }}>
          O plano nasce <b>fora do ar</b>. Depois de criar, a ficha abre aqui para os veículos e
          os conteúdos de revisão — plano sem veículo não alcança ninguém.
        </p>

        <div style={{ display: "flex", gap: 6 }}>
          <Botao
            destaque
            ocupado={incluir.isPending}
            desligado={!forma.titulo.trim()}
            aoClicar={() => {
              aoAvisar(null)
              incluir.mutate({
                rota: "api/planos",
                dados: {
                  titulo: forma.titulo.trim(), montadora: forma.montadora,
                  manual: forma.manual, pagina: forma.pagina,
                  migrado: false, publicado: false,
                },
              }, {
                onSuccess: (p) => {
                  aoAvisar(`Plano ${p.id} criado, fora do ar. Acrescente os veículos.`)
                  aoCriar(p)
                },
                onError: (e) => aoAvisar(mensagem(e)),
              })
            }}
          >
            {incluir.isPending ? "Criando…" : "Criar o plano"}
          </Botao>
          <Botao aoClicar={aoFechar}>Cancelar</Botao>
        </div>
      </Secao>
    </Painel>
  )
}

/** O que o "Gravar" do plano leva: serve para dizer se há alteração não gravada. */
const CAMPOS_DO_PLANO = ["titulo", "pagina", "manual", "montadora"] as const

function FichaDoPlano({ plano, nomes, aoAvisar, aoFechar }: {
  plano: Plano
  nomes: ReturnType<typeof usarNomes>
  aoAvisar: (m: string | null) => void
  aoFechar: () => void
}) {
  const base = `api/planos/${plano.id}`
  const veiculos = usarFilhos<VeiculoVinculado>(["plano-veiculos", plano.id], `${base}/veiculos`)
  const conteudos = usarFilhos<ConteudoDoPlano>(["plano-conteudos", plano.id], `${base}/conteudos`)
  // O combo precisa do acervo inteiro (passa de 200; a busca do seletor é local), então todas as páginas.
  const textos = usarMestreInteiro<TextoDePlano>("planos-textos", "api/planos/textos", { pageSize: 200 })

  const invalidar = [["outros", "planos"],
                     ["outros-filhos", "plano-veiculos", plano.id],
                     ["outros-filhos", "plano-conteudos", plano.id]]
  const { alterar, criar, excluir } = usarGravar(invalidar)
  const publicacao = usarPublicacao(invalidar)

  // O formulário só recomeça quando muda o plano ou o que está GRAVADO (como no Torques). Por
  // referência, publicar ou vincular veículo renovavam a lista e o digitado voltava ao antigo.
  const [forma, setForma] = useState({ ...plano })
  const gravado = JSON.stringify(CAMPOS_DO_PLANO.map((k) => plano[k] ?? ""))
  useEffect(() => setForma({ ...plano }), [plano.id, gravado])
  const mudou = CAMPOS_DO_PLANO.some((k) => (forma[k] ?? "") !== (plano[k] ?? ""))

  /**
   * Os dados do PUT. Sem `publicado`/`publicadoEm`: a ficha não se recomeça mais a cada
   * releitura, então o `publicado` do formulário pode ser o de antes do "Publicar", e
   * mandá-lo tiraria o plano do ar.
   */
  function gravarPlano(aoGravar: () => void) {
    const { publicado: _p, publicadoEm: _e, ...dados } = forma
    alterar.mutate({ rota: base, dados }, {
      onSuccess: aoGravar,
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  // 0 nos anos é "sem limite" para a API (o validador só compara quando os dois vêm).
  const [novoVeiculo, setNovoVeiculo] = useState(VEICULO_VAZIO)
  const [novoConteudo, setNovoConteudo] = useState({
    planoManutencaoTextoId: 0, km: 0, tempo: 0, repetir: false, kmRepeticao: 0, severa: false, obs: "",
  })

  const faltaIntervalo = novoConteudo.repetir && !(novoConteudo.kmRepeticao > 0)

  const textosPorId = useMemo(
    () => new Map(textos.itens.map((t) => [t.id, t.texto])), [textos.itens])

  return (
    <Painel>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
        <b style={{ fontSize: 13.5 }}>{plano.titulo}</b>
        <span className="mono" style={{ fontSize: 11, color: "var(--txt3)" }}>{plano.id}</span>
        <button onClick={aoFechar} aria-label="Fechar" style={{
          marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
          color: "var(--txt3)", fontSize: 16, lineHeight: 1,
        }}>×</button>
      </div>

      <EstadoDePublicacao
        publicado={plano.publicado}
        oQue="este plano"
        ocupado={publicacao.isPending}
        aoTrocar={(publicar) => {
          aoAvisar(null)
          const publicarAgora = () => publicacao.mutate({ base, publicar }, {
            onSuccess: () => aoAvisar(publicar ? "Plano no ar." : "Plano fora do ar."),
            onError: (e) => aoAvisar(mensagem(e)),
          })
          // Pôr no ar o que ainda não foi gravado publicaria a versão antiga: grava antes, e
          // se o PUT falhar não publica.
          if (publicar && mudou) gravarPlano(publicarAgora)
          else publicarAgora()
        }}
      />

      <Secao titulo="O plano">
        <Linha>
          <Campo rotulo="Título">
            <input value={forma.titulo ?? ""} style={estiloCampo}
                   onChange={(e) => setForma({ ...forma, titulo: e.target.value })} />
          </Campo>
          <Campo rotulo="Página" ajuda="Onde está no manual da montadora.">
            <input value={forma.pagina ?? ""} style={estiloCampo}
                   onChange={(e) => setForma({ ...forma, pagina: e.target.value })} />
          </Campo>
        </Linha>
        <Campo rotulo="Manual" ajuda="Endereço do manual de origem.">
          <input value={forma.manual ?? ""} style={estiloCampo}
                 onChange={(e) => setForma({ ...forma, manual: e.target.value })} />
        </Campo>
        <Linha>
          <Campo rotulo="Da montadora">
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, paddingTop: 5 }}>
              <input type="checkbox" checked={Boolean(forma.montadora)}
                     onChange={(e) => setForma({ ...forma, montadora: e.target.checked })} />
              {forma.montadora ? "sim" : "não"}
            </label>
          </Campo>
          {/* A publicacao saiu daqui de proposito. Ela tem controle proprio no
              alto da ficha, com rota propria na API -- ter as duas coisas seria
              dois controles para um estado so, e o do formulario so vale quando
              alguem lembra de apertar Gravar. */}
        </Linha>

        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <Botao destaque ocupado={alterar.isPending} aoClicar={() => {
            aoAvisar(null)
            gravarPlano(() => aoAvisar("Plano gravado."))
          }}>
            {alterar.isPending ? "Gravando…" : "Gravar"}
          </Botao>
          {mudou && (
            <>
              <Botao aoClicar={() => setForma({ ...plano })}>Descartar</Botao>
              <span style={{ fontSize: 11, color: "var(--txt3)", alignSelf: "center" }}>
                Alterações não gravadas.
              </span>
            </>
          )}
          <Botao ocupado={excluir.isPending} aoClicar={() => {
            aoAvisar(null)
            excluir.mutate(base, {
              onSuccess: () => { aoAvisar(`Plano ${plano.id} excluído.`); aoFechar() },
              onError: (e) => aoAvisar(mensagem(e)),
            })
          }}>
            Excluir
          </Botao>
        </div>
      </Secao>

      <Secao titulo="Veículos em que vale" contagem={`${veiculos.data?.length ?? 0}`}>
        <TabelaSimples
          itens={veiculos.data ?? []}
          vazio="Nenhum veículo vinculado — o plano não chega a ninguém assim."
          colunas={[
            { chave: "veiculoModelo", rotulo: "Veículo" },
            { chave: "motorNome", rotulo: "Motor" },
            { chave: "anoInicial", rotulo: "De", numero: true, largura: 70 },
            { chave: "anoFinal", rotulo: "Até", numero: true, largura: 70 },
            {
              chave: "id", rotulo: "", largura: 50,
              render: (v) => <Xis aoClicar={() => {
                aoAvisar(null)
                excluir.mutate(`${base}/veiculos/${v.id}`, {
                  onSuccess: () => aoAvisar("Vínculo removido."),
                  onError: (x) => aoAvisar(mensagem(x)),
                })
              }} />,
            },
          ]}
        />
        <Linha>
          <Escolha rotulo="Veículo" mapa={nomes.veiculo} prefixo="veículo"
                   valor={novoVeiculo.veiculoId}
                   aoMudar={(v) => setNovoVeiculo({ ...novoVeiculo, veiculoId: v })} />
          <Escolha rotulo="Motor" mapa={nomes.motor} prefixo="motor"
                   valor={novoVeiculo.motorId}
                   aoMudar={(v) => setNovoVeiculo({ ...novoVeiculo, motorId: v })} />
          <Campo rotulo="Ano inicial">
            <input type="number" value={novoVeiculo.anoInicial || ""} style={estiloCampo}
                   onChange={(e) => setNovoVeiculo({ ...novoVeiculo, anoInicial: Number(e.target.value) })} />
          </Campo>
          <Campo rotulo="Ano final">
            <input type="number" value={novoVeiculo.anoFinal || ""} style={estiloCampo}
                   onChange={(e) => setNovoVeiculo({ ...novoVeiculo, anoFinal: Number(e.target.value) })} />
          </Campo>
        </Linha>
        {anosInvertidos(novoVeiculo) && (
          <p style={{ margin: "0 0 7px", fontSize: 11, color: "var(--erro)" }}>
            O ano inicial é depois do ano final.
          </p>
        )}
        {/* O motor também é obrigatório: sem ele a API devolve "O motor é obrigatório". */}
        <Botao desligado={novoVeiculo.veiculoId <= 0 || novoVeiculo.motorId <= 0 || anosInvertidos(novoVeiculo)}
               ocupado={criar.isPending} aoClicar={() => {
          aoAvisar(null)
          criar.mutate({ rota: `${base}/veiculos`, dados: novoVeiculo }, {
            onSuccess: () => { setNovoVeiculo(VEICULO_VAZIO); aoAvisar("Veículo vinculado.") },
            onError: (e) => aoAvisar(mensagem(e)),
          })
        }}>
          Vincular veículo
        </Botao>
      </Secao>

      <Secao titulo="Conteúdo do plano" contagem={`${conteudos.data?.length ?? 0} operações`}>
        <p style={{ margin: "0 0 7px", fontSize: 11, color: "var(--txt3)", lineHeight: 1.5, maxWidth: "76ch" }}>
          Cada operação vem de um texto da biblioteca, com a quilometragem e o tempo em que ela
          entra. A média no acervo é de 31 operações por plano.
        </p>
        <TabelaSimples
          itens={conteudos.data ?? []}
          vazio="Este plano ainda não tem operação."
          colunas={[
            { chave: "setor", rotulo: "Setor", largura: 150 },
            { chave: "texto", rotulo: "Operação" },
            { chave: "km", rotulo: "km", numero: true, largura: 90 },
            { chave: "tempo", rotulo: "Meses", numero: true, largura: 80 },
            { chave: "repetir", rotulo: "Repete", largura: 80 },
            { chave: "severa", rotulo: "Severa", largura: 80 },
            {
              chave: "id", rotulo: "", largura: 50,
              render: (c) => <Xis aoClicar={() => {
                aoAvisar(null)
                excluir.mutate(`${base}/conteudos/${c.id}`, {
                  onSuccess: () => aoAvisar("Operação removida."),
                  onError: (x) => aoAvisar(mensagem(x)),
                })
              }} />,
            },
          ]}
        />

        <Linha>
          <Escolha rotulo="Texto da biblioteca" mapa={textosPorId} prefixo="texto"
                   valor={novoConteudo.planoManutencaoTextoId}
                   aoMudar={(v) => setNovoConteudo({ ...novoConteudo, planoManutencaoTextoId: v })} />
          <Campo rotulo="km">
            <input type="number" value={novoConteudo.km || ""} style={estiloCampo}
                   onChange={(e) => setNovoConteudo({ ...novoConteudo, km: Number(e.target.value) })} />
          </Campo>
          <Campo rotulo="Meses">
            <input type="number" value={novoConteudo.tempo || ""} style={estiloCampo}
                   onChange={(e) => setNovoConteudo({ ...novoConteudo, tempo: Number(e.target.value) })} />
          </Campo>
        </Linha>
        <Linha>
          <Campo rotulo="Repete">
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, paddingTop: 5 }}>
              <input type="checkbox" checked={novoConteudo.repetir}
                     onChange={(e) => {
                       const repetir = e.target.checked
                       // Marcar "a cada" já sugere o km da operação como intervalo (o mais comum);
                       // desmarcar zera, para não mandar intervalo de uma repetição que não existe.
                       setNovoConteudo({
                         ...novoConteudo, repetir,
                         kmRepeticao: !repetir ? 0
                           : novoConteudo.kmRepeticao > 0 ? novoConteudo.kmRepeticao : novoConteudo.km > 0 ? novoConteudo.km : 0,
                       })
                     }} />
              a cada
            </label>
          </Campo>
          <Campo rotulo="km da repetição">
            <input type="number" value={novoConteudo.kmRepeticao || ""} style={estiloCampo}
                   disabled={!novoConteudo.repetir}
                   onChange={(e) => setNovoConteudo({ ...novoConteudo, kmRepeticao: Number(e.target.value) })} />
          </Campo>
          <Campo rotulo="Condição severa">
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, paddingTop: 5 }}>
              <input type="checkbox" checked={novoConteudo.severa}
                     onChange={(e) => setNovoConteudo({ ...novoConteudo, severa: e.target.checked })} />
              {novoConteudo.severa ? "sim" : "não"}
            </label>
          </Campo>
        </Linha>
        {/* A API recusa repetição sem intervalo (400), e esse erro só aparecia no rodapé. */}
        {faltaIntervalo && (
          <p style={{ margin: "0 0 7px", fontSize: 11, color: "var(--erro)" }}>
            Informe de quantos em quantos km a operação se repete.
          </p>
        )}
        <Botao
          destaque
          desligado={novoConteudo.planoManutencaoTextoId <= 0 || faltaIntervalo}
          ocupado={criar.isPending}
          aoClicar={() => {
            aoAvisar(null)
            criar.mutate({ rota: `${base}/conteudos`, dados: novoConteudo }, {
              onSuccess: () => {
                setNovoConteudo({ planoManutencaoTextoId: 0, km: 0, tempo: 0, repetir: false,
                                  kmRepeticao: 0, severa: false, obs: "" })
                aoAvisar("Operação incluída.")
              },
              onError: (e) => aoAvisar(mensagem(e)),
            })
          }}
        >
          Incluir operação
        </Botao>
      </Secao>
    </Painel>
  )
}

// ------------------------------------------------------------------ textos

function AbaDeTextos({ aoAvisar }: { aoAvisar: (m: string | null) => void }) {
  // A busca vai ao servidor (`?busca=`): a rota entrega no máximo 200 por página e o acervo passa disso.
  const [busca, setBusca] = useState("")
  const termo = usarTermoAdiado(busca)
  const lista = usarMestre<TextoDePlano>("planos-textos", "api/planos/textos", {
    busca: termo || undefined, pageSize: 200,
  })
  const { criar, alterar, excluir } = usarGravar([["outros", "planos-textos"]])
  const [aberto, setAberto] = useState<TextoDePlano | null>(null)
  const [novo, setNovo] = useState({ texto: "", setor: "", ordem: 0, assuntoId: 0, sistemaId: 0, validado: false })

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
      <Painel>
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--txt2)", lineHeight: 1.6, maxWidth: "78ch" }}>
          A biblioteca de operações de revisão — <b>{lista.total} textos</b>{termo && " na busca"}
          {lista.itens.length < lista.total && (
            <>
              , dos quais <b>{lista.itens.length} aqui</b>: a rota entrega no máximo 200 por
              página. {termo ? "Refine a busca" : "Use a busca"} para chegar no que falta
            </>
          )}
          . São escritos uma vez e reaproveitados nos planos; corrigir um texto aqui corrige em
          todos os planos que o usam.
        </p>

        <Secao titulo="Incluir texto">
          <Linha>
            <Campo rotulo="Setor">
              <input value={novo.setor} style={estiloCampo} placeholder="Sistema elétrico"
                     onChange={(e) => setNovo({ ...novo, setor: e.target.value })} />
            </Campo>
            <Campo rotulo="Operação">
              <input value={novo.texto} style={estiloCampo}
                     placeholder="Inspecionar estado de saúde da bateria"
                     onChange={(e) => setNovo({ ...novo, texto: e.target.value })} />
            </Campo>
          </Linha>
          <Botao destaque desligado={!novo.texto.trim()} ocupado={criar.isPending} aoClicar={() => {
            aoAvisar(null)
            criar.mutate({ rota: "api/planos/textos", dados: novo }, {
              onSuccess: () => {
                setNovo({ texto: "", setor: "", ordem: 0, assuntoId: 0, sistemaId: 0, validado: false })
                aoAvisar("Texto incluído.")
              },
              onError: (e) => aoAvisar(mensagem(e)),
            })
          }}>
            Incluir
          </Botao>
        </Secao>

        <div style={{ maxWidth: 420, marginBottom: 10 }}>
          <FiltroDaLista valor={busca} aoMudar={setBusca} rotulo="Buscar texto"
                         placeholder="Parte do texto da operação…" />
        </div>

        <TabelaSimples
          itens={lista.itens}
          selecionado={aberto?.id ?? null}
          aoAbrir={setAberto}
          vazio={termo ? "Nenhum texto com essa busca." : "Biblioteca vazia."}
          colunas={[
            { chave: "setor", rotulo: "Setor", largura: 170 },
            { chave: "texto", rotulo: "Operação" },
            { chave: "validado", rotulo: "Validado", largura: 90 },
            { chave: "ordem", rotulo: "Ordem", numero: true, largura: 80 },
          ]}
        />

        {aberto && (
          <Secao titulo={`Alterar texto ${aberto.id}`}>
            <Linha>
              <Campo rotulo="Setor">
                <input value={aberto.setor} style={estiloCampo}
                       onChange={(e) => setAberto({ ...aberto, setor: e.target.value })} />
              </Campo>
              <Campo rotulo="Operação">
                <input value={aberto.texto} style={estiloCampo}
                       onChange={(e) => setAberto({ ...aberto, texto: e.target.value })} />
              </Campo>
              <Campo rotulo="Validado">
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, paddingTop: 5 }}>
                  <input type="checkbox" checked={aberto.validado}
                         onChange={(e) => setAberto({ ...aberto, validado: e.target.checked })} />
                  {aberto.validado ? "sim" : "não"}
                </label>
              </Campo>
            </Linha>
            <div style={{ display: "flex", gap: 6 }}>
              <Botao destaque ocupado={alterar.isPending} aoClicar={() => {
                aoAvisar(null)
                alterar.mutate({ rota: `api/planos/textos/${aberto.id}`, dados: aberto }, {
                  onSuccess: () => aoAvisar("Texto gravado."),
                  onError: (e) => aoAvisar(mensagem(e)),
                })
              }}>Gravar</Botao>
              <Botao aoClicar={() => {
                aoAvisar(null)
                excluir.mutate(`api/planos/textos/${aberto.id}`, {
                  onSuccess: () => { aoAvisar("Texto excluído."); setAberto(null) },
                  onError: (e) => aoAvisar(mensagem(e)),
                })
              }}>Excluir</Botao>
              <Botao aoClicar={() => setAberto(null)}>Fechar</Botao>
            </div>
          </Secao>
        )}
      </Painel>
    </div>
  )
}

// ------------------------------------------------------------------ grupos

function AbaDeGrupos({ nomes, aoAvisar }: {
  nomes: ReturnType<typeof usarNomes>
  aoAvisar: (m: string | null) => void
}) {
  const [aberto, setAberto] = useState<GrupoDeManutencao | null>(null)
  const [confirmado, setConfirmado] = useState(false)
  const lista = usarMestre<GrupoDeManutencao>("planos-grupos", "api/planos-manutencao/grupos")

  const [busca, setBusca] = useState("")
  // Os 1.222 grupos vêm de uma vez: a ordem é feita aqui, como em Relés e fusíveis.
  const [ordem, setOrdem] = usarOrdemDaLista("planos-grupos")
  const achados = useMemo(() => {
    const nome = (g: GrupoDeManutencao) => nomeOu(nomes.veiculo, g.veiculoId, "veículo")
    const filtrados = busca.trim() ? lista.itens.filter((g) => casaBusca(busca, nome(g))) : lista.itens
    return ordenarLista(filtrados, ordem, (g) => g.id, nome)
  }, [lista.itens, busca, nomes, ordem])
  // A ficha lê o grupo da lista, e não o guardado no clique: depois de publicar a lista
  // relê, e o estado de publicação acompanha sem reabrir.
  const atual = aberto ? (lista.itens.find((g) => g.id === aberto.id) ?? aberto) : null

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
      <ColunaRecolhivel chave="planos-grupos" titulo="Grupos" largura={320}
                        detalhe={busca.trim() ? `${achados.length} de ${lista.itens.length}` : `${lista.itens.length}`}
                        topo={<FiltroDaLista valor={busca} aoMudar={setBusca} rotulo="Filtrar grupos"
                                             placeholder="Veículo…"
                                             ordem={ordem} aoMudarOrdem={setOrdem} />}>
        {lista.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando os 1.222 grupos…</p>
        ) : (
          <>
            {achados.slice(0, 200).map((g) => (
              <ItemDaLista
                key={g.id}
                ativo={aberto?.id === g.id}
                marcado={g.publicado}
                aoClicar={() => { setAberto(g); setConfirmado(false) }}
                titulo={nomeOu(nomes.veiculo, g.veiculoId, "veículo")}
                detalhe={`versão ${g.versao}`}
              />
            ))}
            {achados.length > 200 && (
              <p style={{ fontSize: 10.5, color: "var(--txt3)" }}>
                {achados.length} grupos; mostrando 200. Use o filtro.
              </p>
            )}
          </>
        )}
      </ColunaRecolhivel>

      {atual ? (
        <ItensDoGrupo
          grupo={atual}
          nomes={nomes}
          confirmado={confirmado}
          aoConfirmar={() => setConfirmado(true)}
          aoAvisar={aoAvisar}
          aoFechar={() => setAberto(null)}
        />
      ) : (
        <Vazio>
          O formato antigo, por veículo e versão. São 1.222 grupos guardando 582.249 itens —
          o maior tem 15.283 numa resposta só, então cada grupo é aberto sob pedido.
        </Vazio>
      )}
    </div>
  )
}

function ItensDoGrupo({ grupo, nomes, confirmado, aoConfirmar, aoAvisar, aoFechar }: {
  grupo: GrupoDeManutencao
  nomes: ReturnType<typeof usarNomes>
  confirmado: boolean
  aoConfirmar: () => void
  aoAvisar: (m: string | null) => void
  aoFechar: () => void
}) {
  const base = `api/planos-manutencao/grupos/${grupo.id}`
  // Só busca depois do aviso: no pior grupo são 15.283 itens e 3,9 MB.
  const itens = usarFilhos<ItemDeManutencao>(["grupo-itens", grupo.id], `${base}/itens`, confirmado)
  const { excluir } = usarGravar([["outros-filhos", "grupo-itens", grupo.id]])
  const publicacao = usarPublicacao([["outros", "planos-grupos"]])

  return (
    <Painel>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
        <b style={{ fontSize: 13.5 }}>{nomeOu(nomes.veiculo, grupo.veiculoId, "veículo")}</b>
        <span style={{ fontSize: 11, color: "var(--txt3)" }}>versão {grupo.versao}</span>
        <button onClick={aoFechar} aria-label="Fechar" style={{
          marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
          color: "var(--txt3)", fontSize: 16, lineHeight: 1,
        }}>×</button>
      </div>

      {/* Fora do portão dos itens: publicar não exige baixar os 15.283 itens antes. */}
      <EstadoDePublicacao
        publicado={grupo.publicado}
        oQue="o grupo"
        ocupado={publicacao.isPending}
        aoTrocar={(publicar) => {
          aoAvisar(null)
          publicacao.mutate({ base, publicar }, {
            onSuccess: () => aoAvisar(publicar ? "Grupo no ar." : "Grupo fora do ar."),
            onError: (e) => aoAvisar(mensagem(e)),
          })
        }}
      />

      {!confirmado ? (
        <>
          <Aviso>
            Esta rota <b>não pagina</b>: ela devolve os itens do grupo de uma vez. A média é de
            493 itens, mas o maior grupo tem <b>15.283</b> e pesa 3,9 MB. Costuma levar um
            segundo — não é travamento.
          </Aviso>
          <Botao destaque aoClicar={aoConfirmar}>Carregar os itens</Botao>
        </>
      ) : itens.isPending ? (
        <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando os itens do grupo…</p>
      ) : itens.error ? (
        <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
          {itens.error instanceof ErroDaApi ? itens.error.message : "Não foi possível ler os itens."}
        </p>
      ) : (
        <Secao titulo="Operações do grupo" contagem={`${(itens.data ?? []).length.toLocaleString("pt-BR")}`}>
          <TabelaSimples
            itens={itens.data ?? []}
            vazio="Este grupo não tem operação."
            colunas={[
              { chave: "motorId", rotulo: "Motor", largura: 190,
                render: (i) => nomeOu(nomes.motor, i.motorId, "motor") },
              { chave: "ano", rotulo: "Ano", numero: true, largura: 75 },
              { chave: "quilometragem", rotulo: "km", numero: true, largura: 95 },
              { chave: "tempoMeses", rotulo: "Meses", numero: true, largura: 80 },
              { chave: "operacao", rotulo: "Operação" },
              { chave: "pagina", rotulo: "Página", largura: 85 },
              {
                chave: "id", rotulo: "", largura: 50,
                render: (i) => <Xis aoClicar={() => {
                  aoAvisar(null)
                  excluir.mutate(`${base}/itens/${i.id}`, {
                    onSuccess: () => aoAvisar("Operação removida."),
                    onError: (x) => aoAvisar(mensagem(x)),
                  })
                }} />,
              },
            ]}
          />
        </Secao>
      )}

      {/* Fora do portao dos itens de proposito: importar planilha nao exige
          baixar os 15.283 itens do grupo antes. */}
      <LoteDoPlano grupoId={grupo.id} aoAvisar={aoAvisar} />
    </Painel>
  )
}

function Xis({ aoClicar }: { aoClicar: () => void }) {
  return (
    <button onClick={aoClicar} aria-label="Remover" style={{
      border: 0, background: "transparent", cursor: "pointer",
      color: "var(--txt3)", fontSize: 13, lineHeight: 1,
    }}>
      ×
    </button>
  )
}
